import axios from "axios";
import {
  createDownloadLink,
  createFetchOptions,
  getCSRFToken,
  handle401Error,
  handleCSRFError,
  handleFetchCSRFError,
  handleFetchResponse,
  hasAuthError,
  hasCSRFError,
} from "./utils";
import { ClientActionPostPayload } from "../features/ClientActions/types/clientActionPostPayload";

/**
 * Axios instance for making authenticated requests
 */
const axiosInstance = axios.create();

/**
 * Interceptor to handle CSRF token refreshing and authentication errors
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle CSRF errors
    if (hasCSRFError(error) && error.config && !error.config._retry) {
      error.config._retry = true;
      const response = await handleCSRFError(error, axiosInstance);
      if (response) return response;
    }

    // Handle authentication errors
    if (hasAuthError(error)) {
      handle401Error();
    }

    return Promise.reject(error);
  },
);

// Add a request interceptor to ensure credentials are always included
axiosInstance.interceptors.request.use((config) => {
  config.withCredentials = true;
  return config;
});

/**
 * Makes an authenticated GET request
 * @param {string} url - The URL to request
 * @param {AbortSignal} [signal] - Optional signal to abort the request
 * @returns {Promise<Response>} The fetch response
 */
export async function getAuthorized(
  url: string,
  signal?: AbortSignal,
  redirect_on_401 = true,
): Promise<Response> {
  const response = await fetch(url, {
    method: "GET",
    mode: "cors",
    credentials: "include",
    signal,
    headers: {
      Accept: "application/json",
    },
  });

  if (!redirect_on_401 && response.status === 401) {
    throw new Error("Unauthorized");
  }

  return handleFetchResponse(response);
}

/**
 * Makes an authenticated POST request with FormData
 * @param {string} url - The URL to request
 * @param {FormData} formData - The form data to send
 * @returns {Promise<any>} The response data
 */
export async function postFormAuthorized(
  url: string,
  formData: FormData,
): Promise<any> {
  const csrfToken = getCSRFToken();
  const response = await axiosInstance.post(url, formData, {
    withCredentials: true,
    headers: {
      "X-CSRF-Token": csrfToken,
    },
  });
  return response.data;
}

export async function postClientAction<T extends ClientActionPostPayload>(
  url: string,
  data: T,
): Promise<any> {
  return postAuthorized(url, data);
}

/**
 * Makes an authenticated POST request with data
 * @param {string} url - The URL to request
 * @param {any} data - The data to send
 * @returns {Promise<any>} The response data
 */
export async function postAuthorized(url: string, data: any): Promise<any> {
  const csrfToken = getCSRFToken();
  const response = await axiosInstance.post(url, data, {
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
      Accept: "application/json",
    },
  });
  return response.data;
}

/**
 * Makes an authenticated PUT request with data
 * @param {string} url - The URL to request
 * @param {any} data - The data to send
 * @returns {Promise<any>} The response data
 */
export async function putAuthorized(url: string, data: any): Promise<any> {
  const csrfToken = getCSRFToken();
  const response = await axiosInstance.put(url, data, {
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
      Accept: "application/json",
    },
  });

  if (response.status === 200 && response.data === null) {
    return {} as any;
  }
  return response.data;
}

/**
 * Makes an authenticated PATCH request with data
 * @param {string} url - The URL to request
 * @param {any} data - The data to send
 * @returns {Promise<any>} The response data
 */
export async function patchAuthorized(url: string, data: any): Promise<any> {
  const csrfToken = getCSRFToken();
  const response = await axiosInstance.patch(url, data, {
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
      Accept: "application/json",
    },
  });

  if (response.status === 200 && response.data === null) {
    return {} as any;
  }
  return response.data;
}

/**
 * Makes an authenticated POST request with JSON data
 * @param {string} url - The URL to request
 * @param {object} data - The JSON data to send
 * @returns {Promise<any>} The response data
 */
export async function postAuthorizedJSON(
  url: string,
  data: object,
): Promise<any> {
  return postAuthorized(url, data);
}

/**
 * Makes an authenticated PUT request with JSON data
 * @param {string} url - The URL to request
 * @param {object} data - The JSON data to send
 * @returns {Promise<any>} The response data
 */
export async function putAuthorizedJSON(
  url: string,
  data: object,
): Promise<any> {
  return putAuthorized(url, data);
}

/**
 * Makes an authenticated DELETE request
 * @param {string} url - The URL to request
 * @returns {Promise<any>} The response data
 */
export async function deleteAuthorized(url: string): Promise<any> {
  const csrfToken = getCSRFToken();
  const response = await axiosInstance.delete(url, {
    withCredentials: true,
    headers: {
      "X-CSRF-Token": csrfToken,
      Accept: "application/json",
    },
  });
  return response.data;
}

export async function deleteWithBodyAuthorized(url: string, data: unknown): Promise<any> {
  const csrfToken = getCSRFToken();
  const response = await axiosInstance.delete(url, {
    withCredentials: true,
    headers: {
      "X-CSRF-Token": csrfToken,
      Accept: "application/json",
    },
    data,
  });
  return response.data;
}

/**
 * Makes an authenticated POST request with form data and streaming response
 * @param {string} url - The URL to request
 * @param {object} formData - The data to send
 * @returns {Promise<Response>} The fetch response
 */
export async function postFormAuthorizedStreaming(
  url: string,
  formData: FormData,
): Promise<Blob> {
  const csrfToken = getCSRFToken();
  const response = await axiosInstance.post(url, formData, {
    withCredentials: true,
    responseType: "blob",
    headers: {
      "X-CSRF-Token": csrfToken,
    },
  });
  return response.data;
}

/**
 * Makes an authenticated POST request with streaming response
 * @param {string} url - The URL to request
 * @param {object} data - The data to send
 * @param {AbortSignal} [signal] - Optional signal to abort the request
 * @returns {Promise<Response>} The fetch response
 */
export async function postAuthorizedStreaming(
  url: string,
  data: object,
  signal?: AbortSignal,
): Promise<Response> {
  // Create fetch request options with CSRF token
  const options = createFetchOptions("POST", data, signal);

  // Ensure credentials are included
  options.credentials = "include";

  // Add Accept header
  if (!options.headers) {
    options.headers = {};
  }
  (options.headers as Record<string, string>)["Accept"] = "application/json";

  let response = await fetch(url, options);

  if (response.status === 403) {
    const newResponse = await handleFetchCSRFError(response, url, data, signal);
    if (newResponse) {
      response = newResponse;
    }
  }

  return handleFetchResponse(response);
}

/**
 * Downloads a file from an authenticated endpoint
 * @param {string} href - The URL to download from
 * @param {string} filename - The name to save the file as
 * @returns {Promise<void>}
 */
export async function download(href: string, filename?: string): Promise<void> {
  try {
    const response = await getAuthorized(href);
    if (!response.ok) {
      let detail: string | undefined;
      try {
        const body = await response.json();
        detail = body?.detail;
      } catch {}
      throw new Error(detail ?? `Download failed with status ${response.status}`);
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    let finalFilename = filename;

    // If no filename provided, try to extract from Content-Disposition header
    if (!finalFilename) {
      const contentDisposition = response.headers.get("content-disposition");
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (filenameMatch && filenameMatch[1]) {
          finalFilename = filenameMatch[1].replace(/['"]/g, "");
        }
      }
    }

    createDownloadLink(url, finalFilename);
  } catch (error) {
    console.error("Download failed", error);
    throw error;
  }
}
